import { useCallback, useMemo } from 'react';

import { DateTime } from 'luxon';
import { useLocalStorage } from 'ui/Hooks';

import { CartId, CartsStorage } from '../types/storage.types';

/** Uses local storage to store basic cart information */
const useCartStorage = () => {
  const [carts, setValue, removeValue] = useLocalStorage<CartsStorage>({
    key: 'givebacks-shop-carts',
    // Default to no stored stores
    defaultValue: [],
  });

  /** Given a cartId updates the stored carts information */
  const updateCart = useCallback(
    ({ cartId, cart }: { cartId: CartId; cart: CartsStorage[number] }) => {
      const now = DateTime.now();
      setValue((prev) =>
        prev.map((_cart) => {
          if (_cart.cartId === cartId) {
            const itemsCount = cart.itemsCount || 0;
            return { ..._cart, lastModified: now.toISO(), itemsCount };
          }
          return _cart;
        })
      );
    },
    [setValue]
  );

  /** Adds a new cart to storage */
  const addCart = useCallback(
    ({ cart }: { cart: CartsStorage[number] }) => {
      const now = DateTime.now().toISO();
      const itemsCount = cart.itemsCount || 0;
      setValue([...carts, { ...cart, lastModified: now, itemsCount }]);
    },
    [carts, setValue]
  );

  /** Removes the specified cart from storage */
  const removeCart = (cartId: CartId) => {
    return setValue(carts.filter((cart) => cart.cartId !== cartId));
  };

  /** Count of each current cart, unique by cartId  */
  const cartCount = carts.length;

  /** Count of all items from all carts */
  const cartsItemsCount = carts.reduce((acc, cart) => acc + (cart.itemsCount || 0), 0);

  const cartsGroupedByStore = useMemo(() => {
    const cartsById = carts.reduce((acc, cart) => {
      acc[cart.storeId] = cart;
      return acc;
    }, {});

    return Object.entries(cartsById).map(([storeId, cart]) => {
      return { storeId, cart } as { storeId: string; cart: CartsStorage[number] };
    });
  }, [carts]);

  /** Returns Carts by store and allows ordering */
  const getCartsByStore = useCallback(
    ({ orderBy = 'lastModified' }: { orderBy?: keyof CartsStorage[number] } = {}) => {
      // eslint-disable-next-line id-length -- Short variable names are acceptable in sort comparisons
      return cartsGroupedByStore.sort((a, b) => {
        if (orderBy === 'lastModified') {
          return (
            DateTime.fromISO(b.cart.lastModified || '').toMillis() -
            DateTime.fromISO(a.cart.lastModified || '').toMillis()
          );
        }
        return 0;
      });
    },
    [cartsGroupedByStore]
  );

  return {
    carts,
    cartsGroupedByStore,
    updateCart,
    removeCart,
    addCart,
    /** Removes all cart storage */
    clearCart: removeValue,
    cartCount,
    cartsItemsCount,
    getCartsByStore,
  };
};
export { useCartStorage };
